import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import MatchScorer from '../components/MatchScorer';

const ORGA_URL = process.env.REACT_APP_ORGANIZER_API_URL || 'http://localhost:5001';

// ── helpers ──
function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}
function toneOf(name) {
  let h = 0; for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) % 6; return h + 1;
}
function Avatar({ name, size = 44 }) {
  return (
    <span className={`avatar avatar-${toneOf(name)}`} style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: size * 0.34, color: '#fff',
    }}>{initials(name)}</span>);
}
function LiveDot({ color = 'var(--coral)', size = 8 }) {
  return <span className="live-dot" style={{ width: size, height: size, background: color, borderRadius: '50%', display: 'inline-block' }} />;
}
function poolLabel(name) { return (name || '').replace(/^Poule\b/, 'Pool'); }
function setsWon(sets, side) {
  if (!Array.isArray(sets)) return 0;
  let n = 0; for (const s of sets) { if (side === 'a' && s.a > s.b) n++; if (side === 'b' && s.b > s.a) n++; } return n;
}

const ROUND_LABEL = { finale: 'Finale', demi: 'Demie', quart: 'Quart', '8e': '8e', '16e': '16e', poule: 'Poule' };
const ROUND_ORDER = ['16e', '8e', 'quart', 'demi', 'finale'];
const ROUND_TITLE = { '16e': '16es', '8e': '8es', quart: 'Quarts', demi: 'Demies', finale: 'Finale' };
const BRACKET_TITLE = { principale: 'Tableau principal', consolante_principale: 'Consolante principale', consolante: 'Consolante' };
const BRACKET_SLOT = 72; // hauteur du créneau du 1er tour ; double à chaque tour pour centrer le match suivant entre ses 2 sources.
// L'écart (créneau - hauteur carte) reste centré des 2 côtés → augmenter cette constante crée un espace vertical net entre les cartes sans jamais casser le centrage (la relation "double à chaque tour" est inchangée).

export default function Live() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, live: false, data: null, err: '' });
  const [scorerMatch, setScorerMatch] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${ORGA_URL}/api/me/live`, { headers: { Authorization: `Bearer ${token}` } });
      setState({ loading: false, live: !!res.data.live, data: res.data, err: '' });
    } catch (e) {
      // On garde les dernières données connues (stale-while-error) et on signale la coupure réseau.
      setState(s => ({ ...s, loading: false, err: 'Connexion impossible' }));
    }
  }, []);

  // Temps réel par interrogation (reflète les « Valider » de l'orga), suspendu en arrière-plan (batterie mobile).
  useVisiblePolling(load, 15000);

  if (state.loading) {
    return <Shell><p className="muted" style={{ padding: 40, textAlign: 'center' }}>Chargement…</p></Shell>;
  }

  // Pas de tournoi en cours (ou API injoignable) → même état accueillant qui explique l'onglet.
  if (!state.live || !state.data) {
    const failed = !!state.err;
    return (
      <Shell>
        <div style={{ padding: '64px 28px 40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div aria-hidden="true" style={{ fontSize: 46, marginBottom: 16 }}>🏐</div>
          <h2 style={{ margin: '0 0 10px', fontSize: 21 }}>Pas de tournoi en cours</h2>
          <p className="muted" style={{ margin: 0, maxWidth: 300, lineHeight: 1.55, fontSize: 14 }}>
            L'onglet <strong style={{ color: 'var(--text)' }}>Live</strong> t'affiche ta prochaine action — contre qui, sur quel terrain, jouer ou arbitrer — dès qu'un organisateur lance ton tournoi.
          </p>
          <button onClick={() => navigate('/recherche')} style={btnCta}>Trouver un tournoi</button>
          {failed && (
            <button onClick={load} style={{ marginTop: 16, background: 'none', border: 'none', color: 'var(--text-3)', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>
              Reconnexion… réessayer
            </button>
          )}
        </div>
      </Shell>);
  }

  const d = state.data;
  const myIds = new Set(d.myTeamIds || []);
  const poolNames = d.poolTeamNames || {};
  const nameOf = id => poolNames[id] || '—';

  // Prochaine action du joueur (hero) — hors finales, ignore les matchs de tableau (arbre) obsolètes en base.
  const inFinals = d.tournament?.stage === 'finales';
  const myMatches = inFinals ? (d.matches || []) : (d.matches || []).filter(m => m.round === 'poule' && !m.bracket);
  const next = myMatches.find(m => m.status !== 'termine') || null;

  const phaseLabel = inFinals
    ? 'Phase finale'
    : (next
      ? (next.round === 'poule' && d.pool ? `Phase de groupe ${d.pool.phase}` : (ROUND_LABEL[next.round] || 'Match'))
      : (d.pool ? `Phase de groupe ${d.pool.phase}` : ''));
  const poolName = inFinals ? '' : poolLabel(d.pool?.name || '');
  const courtName = d.pool?.courtName || '';

  return (
    <div className="screen">
      {/* Header (fixe, hors zone de défilement) */}
      <div style={{ padding: '8px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--coral)' }}>
            <LiveDot /> Tournoi en cours
          </span>
          <h1 style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.tournament?.name}</h1>
        </div>
        {(phaseLabel || poolName) && <span className="chip" style={chipCyan}>{phaseLabel}{poolName ? ` · ${poolName}` : ''}{courtName ? ` · ${courtName}` : ''}</span>}
      </div>

      <div className="screen-scroll">
      {/* Next action hero */}
      {next ? <NextHero next={next} myIds={myIds} onOpenScorer={() => setScorerMatch(next)} /> : (
        <div style={{ padding: '4px 20px 0' }}>
          <div style={{ ...heroCard, color: '#fff' }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>Aucun match à venir pour l'instant. Reste prêt — l'organisateur enchaîne les matchs.</span>
          </div>
        </div>)}

      {/* Tableau final, en phase finale */}
      {inFinals && <BracketSection bracket={d.bracket} myIds={myIds} />}

      {/* Ma pool — tableau toujours visible dès qu'une pool existe, même à 0 match joué (standings vides). */}
      {!inFinals && d.pool && (() => {
        const rows = d.pool.standings && d.pool.standings.length > 0
          ? d.pool.standings
          : (d.pool.teamIds || []).map(teamId => ({ teamId, wins: 0, pointDiff: 0 }));
        return (<>
        <SectionTitle title={`Ma pool · ${poolLabel(d.pool.name)}`} />
        <div style={{ padding: '0 20px' }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
              <span className="mono" style={{ width: 20, color: 'var(--text-3)' }}>#</span>
              <span className="mono" style={{ flex: 1, color: 'var(--text-3)' }}>Équipe</span>
              <span className="mono" style={{ width: 30, textAlign: 'center', color: 'var(--text-3)' }}>V</span>
              <span className="mono" style={{ width: 60, textAlign: 'right', color: 'var(--text-3)' }}>DIFF PTS</span>
            </div>
            {rows.map((p, i) => {
              const you = myIds.has(p.teamId);
              return (
                <div key={p.teamId} style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: i < rows.length - 1 ? '1px solid var(--line)' : 'none', background: you ? 'rgba(245,166,35,0.08)' : 'transparent' }}>
                  <span className="display" style={{ width: 20, fontSize: 15, color: i < 2 ? 'var(--text)' : 'var(--text-3)' }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: you ? 800 : 600, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nameOf(p.teamId)}{you && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--sun-2)', marginLeft: 7 }}>TOI</span>}
                  </span>
                  <span className="mono" style={{ width: 30, textAlign: 'center', fontWeight: 700, color: 'var(--text)' }}>{p.wins}</span>
                  <span className="mono" style={{ width: 60, textAlign: 'right', color: (p.pointDiff || 0) >= 0 ? 'var(--lime)' : 'var(--coral)' }}>{(p.pointDiff || 0) >= 0 ? '+' : ''}{p.pointDiff || 0}</span>
                </div>);
            })}
            <div style={{ padding: '9px 14px', background: 'var(--ink-3)', fontSize: 11.5, color: 'var(--text-3)', fontWeight: 600 }}>
              Les 2 premiers accèdent à la phase finale principale.
            </div>
          </div>
        </div>
      </>);
      })()}

      {/* Matchs de la pool (tous, pas seulement les miens) */}
      {!inFinals && d.poolMatches && d.poolMatches.length > 0 && (<>
        <SectionTitle title="Matchs de la pool" />
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {d.poolMatches.map((m, i) => {
            const involved = m.isPlaying || m.isReferee;
            const finished = m.status === 'termine';
            const tappable = involved && !finished;
            const score = finished
              ? (Array.isArray(m.sets) && m.sets.length === 1
                ? `${m.sets[0].a}–${m.sets[0].b}`
                : `${setsWon(m.sets, 'a')}–${setsWon(m.sets, 'b')}`)
              : 'à venir';
            return (
              <div key={m.id || i} className="card" onClick={tappable ? () => setScorerMatch(m) : undefined}
                style={{ ...cardRow, padding: '11px 14px', background: involved ? 'rgba(245,166,35,0.08)' : undefined, cursor: tappable ? 'pointer' : undefined }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span style={myIds.has(m.teamAId) ? { fontWeight: 800, color: 'var(--sun-2)' } : undefined}>{m.teamA}</span>
                    {' '}<span style={{ color: 'var(--text-3)', fontWeight: 500 }}>vs</span>{' '}
                    <span style={myIds.has(m.teamBId) ? { fontWeight: 800, color: 'var(--sun-2)' } : undefined}>{m.teamB}</span>
                    {involved && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--sun-2)', marginLeft: 7 }}>{m.isPlaying ? 'TOI' : 'TU ARBITRES'}</span>}
                  </span>
                  <span className="mono" style={{ color: 'var(--text-3)', marginTop: 3, fontSize: 11 }}>
                    {m.courtName ? `${m.courtName} · ` : ''}arb. {m.referee}
                  </span>
                </div>
                <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: finished ? 'var(--text)' : (tappable ? 'var(--sun-2)' : 'var(--text-3)') }}>{score}</span>
              </div>);
          })}
        </div>
      </>)}

      {/* Classement général de la catégorie — affiché quand ma poule ne me concerne plus */}
      {!inFinals && d.categoryRanking && d.categoryRanking.length > 0 && d.poolMatches && d.poolMatches.every(m => !(m.isPlaying || m.isReferee) || m.status === 'termine') && (<>
        <SectionTitle title="Classement général" />
        <div style={{ padding: '0 20px' }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
              <span className="mono" style={{ width: 20, color: 'var(--text-3)' }}>#</span>
              <span className="mono" style={{ flex: 1, color: 'var(--text-3)' }}>Équipe</span>
              <span className="mono" style={{ width: 30, textAlign: 'center', color: 'var(--text-3)' }}>V</span>
              <span className="mono" style={{ width: 36, textAlign: 'right', color: 'var(--text-3)' }}>Pts</span>
              <span className="mono" style={{ width: 50, textAlign: 'right', color: 'var(--text-3)' }}>Diff</span>
            </div>
            {d.categoryRanking.map((r, i) => {
              const you = myIds.has(r.teamId);
              return (
                <div key={r.teamId} style={{ display: 'flex', alignItems: 'center', padding: '11px 14px', borderBottom: i < d.categoryRanking.length - 1 ? '1px solid var(--line)' : 'none', background: you ? 'rgba(245,166,35,0.08)' : 'transparent' }}>
                  <span className="display" style={{ width: 20, fontSize: 15, color: i < 2 ? 'var(--text)' : 'var(--text-3)' }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: you ? 800 : 600, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.name}{you && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--sun-2)', marginLeft: 7 }}>TOI</span>}
                  </span>
                  <span className="mono" style={{ width: 30, textAlign: 'center', fontWeight: 700, color: 'var(--text)' }}>{r.wins}</span>
                  <span className="mono" style={{ width: 36, textAlign: 'right', color: 'var(--text)' }}>{r.pointsWon}</span>
                  <span className="mono" style={{ width: 50, textAlign: 'right', color: (r.pointDiff || 0) >= 0 ? 'var(--lime)' : 'var(--coral)' }}>{(r.pointDiff || 0) >= 0 ? '+' : ''}{r.pointDiff || 0}</span>
                </div>);
            })}
          </div>
        </div>
      </>)}

      <div style={{ height: 28 }} />
      </div>

      {scorerMatch && (
        <MatchScorer
          match={scorerMatch}
          tournamentId={d.tournament?.id}
          format={d.format}
          onClose={() => setScorerMatch(null)}
          onSubmitted={load}
        />
      )}
    </div>);
}

function NextHero({ next, myIds, onOpenScorer }) {
  const isPlay = next.isPlaying;
  // Le modèle de match ne distingue que 'a_venir' / 'termine' : `next` est toujours à venir ici.
  const heroLabel = isPlay ? 'À toi de jouer' : 'Tu arbitres';
  const ctaLabel = isPlay ? 'Saisir le score' : 'Arbitrer le match';
  const youA = myIds.has(next.teamAId);
  return (
    <div style={{ padding: '4px 20px 0' }}>
      <div style={heroCard}>
        <div style={{ position: 'absolute', top: -80, right: -56, width: 210, height: 210, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,166,35,0.5), transparent 70%)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#FFD37A' }}>
            <LiveDot color="#FF6B5A" /> {heroLabel}
          </span>
          {next.courtName && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'rgba(245,166,35,0.92)', color: '#1A1206', fontSize: 12.5, fontWeight: 800 }}><span aria-hidden="true">📍</span> {next.courtName}</span>}
        </div>
        <div style={{ marginTop: isPlay ? 16 : 8, borderRadius: 'var(--r-md)', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.13)', position: 'relative' }}>
          <TeamRow name={next.teamA} you={youA} />
          <div style={{ position: 'relative', height: 1, background: 'rgba(255,255,255,0.13)' }}>
            <span className="display" style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', background: 'var(--text)', padding: '0 8px', fontSize: 12, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)' }}>VS</span>
          </div>
          <TeamRow name={next.teamB} you={!youA && myIds.has(next.teamBId)} />
        </div>
        {isPlay && next.referee && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 13, gap: 8, position: 'relative' }}>
            <span aria-hidden="true" style={{ color: '#FFD37A' }}>🔔</span>
            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.72)' }}>Arbitre · <strong style={{ color: '#fff', fontWeight: 700 }}>{next.referee}</strong></span>
          </div>)}
        <button onClick={onOpenScorer} style={{ marginTop: 15, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', borderRadius: 'var(--r-md)', background: 'var(--sun)', color: '#1A1206', fontWeight: 800, fontSize: 14.5, position: 'relative', border: 'none', cursor: 'pointer' }}>
          {ctaLabel} →
        </button>
      </div>
    </div>);
}

function TeamRow({ name, you }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '13px 14px', gap: 12 }}>
      <Avatar name={name} size={40} />
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {you && <span style={{ fontSize: 11, fontWeight: 700, color: '#FFD37A', marginTop: 1 }}>Ton équipe</span>}
      </div>
    </div>);
}

function BracketSection({ bracket, myIds }) {
  if (!bracket || !bracket.matches || bracket.matches.length === 0) {
    return (
      <div style={{ padding: '4px 20px 0' }}>
        <p className="muted" style={{ fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Le tableau final n'est pas encore disponible.</p>
      </div>);
  }
  // Ne garder que le(s) tableau(x) où joue une équipe du joueur ; sinon (cas limite) tout afficher.
  const allBrackets = [...new Set(bracket.matches.map(m => m.bracket))];
  const myBrackets = allBrackets.filter(bk => bracket.matches.some(m => m.bracket === bk && (myIds.has(m.teamAId) || myIds.has(m.teamBId))));
  const brackets = myBrackets.length > 0 ? myBrackets : allBrackets;
  return brackets.map(bk => {
    const bm = bracket.matches.filter(m => m.bracket === bk);
    const rounds = ROUND_ORDER.filter(r => bm.some(m => m.round === r));
    return (
      <React.Fragment key={bk}>
        <SectionTitle title={BRACKET_TITLE[bk] || bk} />
        <div style={{ padding: '0 20px', display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
          {rounds.map((r, ri) => (
            <div key={r} style={{ display: 'flex', flexDirection: 'column', minWidth: 160, flexShrink: 0 }}>
              <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center', marginBottom: 8 }}>{ROUND_TITLE[r] || r}</div>
              {bm.filter(m => m.round === r).sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })).map(m => (
                // Créneau qui double à chaque tour → match centré à mi-hauteur entre ses 2 matchs sources.
                <div key={m.id} style={{ height: BRACKET_SLOT * Math.pow(2, ri), display: 'flex', alignItems: 'center' }}>
                <div className="card" style={{ padding: 0, overflow: 'hidden', width: '100%' }}>
                  {['a', 'b'].map(side => {
                    const tid = side === 'a' ? m.teamAId : m.teamBId;
                    const name = side === 'a' ? m.teamA : m.teamB;
                    const win = m.winnerTeamId && m.winnerTeamId === tid;
                    const you = tid && myIds.has(tid);
                    return (
                      <div key={side} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '7px 9px', fontSize: 12,
                        borderBottom: side === 'a' ? '1px solid var(--line)' : 'none',
                        background: you ? 'rgba(245,166,35,0.08)' : 'transparent',
                        color: win ? 'var(--sun)' : 'var(--text)', fontWeight: win ? 800 : 600 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{name || 'À définir'}</span>
                        <span>{setsWon(m.sets, side)}</span>
                      </div>
                    );
                  })}
                </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </React.Fragment>);
  });
}

function SectionTitle({ title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 20px', marginTop: 24, marginBottom: 10 }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-2)' }}>{title}</h3>
      {right && <span className="mono" style={{ color: 'var(--text-3)' }}>{right}</span>}
    </div>);
}

function Shell({ children }) {
  // Même structure que les autres écrans : header/contenu défilant DANS le shell .App (height:100dvh, overflow:hidden).
  return (
    <div className="screen">
      <div className="screen-scroll">{children}</div>
    </div>);
}

const heroCard = { position: 'relative', overflow: 'hidden', background: 'var(--text)', color: '#fff', borderRadius: 'var(--r-xl)', padding: '18px 20px' };
const cardRow = { display: 'flex', alignItems: 'center', padding: 12, gap: 12 };
const chipCyan = { flexShrink: 0, background: 'rgba(10,143,168,0.12)', color: 'var(--cyan-2)' };
const btnCta = { marginTop: 22, padding: '13px 24px', borderRadius: 14, border: 'none', background: 'var(--sun)', color: '#2A1B00', fontWeight: 800, fontSize: 14.5, cursor: 'pointer', boxShadow: '0 8px 20px rgba(245,166,35,0.4)' };
